import cn from 'classnames';
import AdOverlay from 'components/AdOverlay';
import * as SafeZone from 'components/Interactive/SafeZone';
import { MenuShare, withMenu } from 'components/Menu';
import ShareWidgetDefault from 'components/ShareWidget';
import WaterMark from 'components/WaterMark';
import PlayerSkinEnded from 'containers/PlayerSkin/PlayerSkinEnded';
import { TranslateMapContext } from 'contexts/TranslateMapContext';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { PureComponent, Suspense } from 'react';
import { withTheme } from 'styled-components';
import * as Cookie from 'tiny-cookie';
import iOSFullscreenWatcher from 'utils/iOSFullscreenWatcher';
import { SkinResizeObserver } from '../../components/newSkin/SkinResizeObserver';
import SkipIntro from '../../components/SkipIntro';
import { playerMethods } from '../../initSkin';
import { getIsDesktopEpgPresent } from '../../utils/isDesktopEpgPresent';
import {
  MainWrapper,
  MessageContainerStyled,
  PlayerSkinStyled,
  ShareContainer,
  Substrate,
  WaterMarkWrap,
} from './styles/PlayerSkinStyled';
import { Subtitles } from './Subtitles';
import { SwipeChannelSwitch } from './SwipeChannelSwitch';

/*
import logger from 'utils/logger';
*/

/*
const skinLogger = logger('PlayerSkin');
*/

const isAllwaysShowSkinDev = window.location.hash.includes(
  'alwaysshowskin=true'
);
const HIDE_SKIN_TIME = 6000;
let SKIN_TIMER_ID = null;
let STARTUP_SKIN_TIMER_ID = null;

class PlayerSkin extends PureComponent {
  static contextType = TranslateMapContext;

  static propTypes = {
    // player's properties
    currentTime: PropTypes.number.isRequired,
    duration: PropTypes.number.isRequired,
    loadedFraction: PropTypes.number.isRequired,
    volume: PropTypes.number.isRequired,
    paused: PropTypes.bool.isRequired,
    ended: PropTypes.bool.isRequired,
    muted: PropTypes.bool.isRequired,
    timeToAdStart: PropTypes.number,
    subtitlesText: PropTypes.string,
    qualityList: PropTypes.array.isRequired,
    audioTrackList: PropTypes.array.isRequired,
    subtitlesList: PropTypes.array.isRequired,
    thumbnails: PropTypes.array.isRequired,
    isBuffering: PropTypes.bool.isRequired,
    audioTrack: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    isFavorite: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
      .isRequired,
    subtitles: PropTypes.string,
    quality: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    chromecastState: PropTypes.string.isRequired,
    messageModeData: PropTypes.oneOfType([PropTypes.object, PropTypes.bool])
      .isRequired,
    emitChromecastAction: PropTypes.func.isRequired,
    emitSkinVisibilityBeginChange: PropTypes.func.isRequired,
    emitSafeZoneSizeChanged: PropTypes.func.isRequired,
    smallMode: PropTypes.bool.isRequired,

    // player's functions
    play: PropTypes.func.isRequired,
    pause: PropTypes.func.isRequired,
    seek: PropTypes.func.isRequired,
    mute: PropTypes.func.isRequired,
    unMute: PropTypes.func.isRequired,
    setVolume: PropTypes.func.isRequired,
    setAudioTrack: PropTypes.func.isRequired,
    setQuality: PropTypes.func.isRequired,
    setSubtitles: PropTypes.func.isRequired,
    setMessageContainer: PropTypes.func.isRequired,
    toggleFavorite: PropTypes.func.isRequired,
    prevChannel: PropTypes.func,
    nextChannel: PropTypes.func,
    prevProgram: PropTypes.func,
    nextProgram: PropTypes.func,
    backOnAir: PropTypes.func,
    skipIntroStart: PropTypes.func,
    disableLoader: PropTypes.func,
    onSharePopupOpen: PropTypes.func,

    // interactive properties
    isInteractiveFullscreen: PropTypes.bool,

    // advert properties
    adVastData: PropTypes.object,
    adType: PropTypes.string,
    adCurrentTime: PropTypes.number.isRequired,
    adPlaying: PropTypes.bool.isRequired,
    adTimeLabels: PropTypes.array,
    // adContainer: PropTypes.instanceOf(HTMLElement),
    adActionClose: PropTypes.func.isRequired,
    adActionClick: PropTypes.func.isRequired,
    adActionResume: PropTypes.func.isRequired,

    // stream properties
    type: PropTypes.string.isRequired,
    isWatermarkClickable: PropTypes.bool.isRequired,
    srcWaterMark: PropTypes.string.isRequired,
    shareLink: PropTypes.string.isRequired,
    contentType: PropTypes.string.isRequired,
    rootVideoId: PropTypes.number,
    title: PropTypes.string,
    programDuration: PropTypes.number,
    programPosition: PropTypes.number,
    currentProgramTime: PropTypes.number,
    nextObject: PropTypes.shape({
      title: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      image: PropTypes.string,
    }),
    prevObject: PropTypes.shape({
      title: PropTypes.string,
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      image: PropTypes.string,
    }),
    channelState: PropTypes.string,
    currentChannel: PropTypes.object,
    nextProgramObject: PropTypes.object,
    prevProgramObject: PropTypes.object,
    program: PropTypes.object,
    autoQualityIndex: PropTypes.number,

    // service functions & callbacks
    translate: PropTypes.func.isRequired,
    setSkipAdProperty: PropTypes.func.isRequired,
    onRetry: PropTypes.func.isRequired,
    onShareOpen: PropTypes.func.isRequired,
    onShareClose: PropTypes.func.isRequired,
    onEpisodeOpen: PropTypes.func.isRequired,
    onEpisodeClose: PropTypes.func.isRequired,
    onCloseClick: PropTypes.func.isRequired,
    onNextClick: PropTypes.func.isRequired,
    onPrevClick: PropTypes.func.isRequired,
    onBitrateBySubscriptionButtonClick: PropTypes.func.isRequired,

    // miscellaneous properties
    isFullscreen: PropTypes.bool.isRequired,
    isMobile: PropTypes.bool.isRequired,
    isAndroid: PropTypes.bool.isRequired,
    playerVideoElement: PropTypes.instanceOf(HTMLVideoElement),
    enterFullscreen: PropTypes.func.isRequired,
    exitFullscreen: PropTypes.func.isRequired,
    showCloseButton: PropTypes.bool,
    showSkipIntroButton: PropTypes.bool,
    skipIntroSkipped: PropTypes.bool,
    showFavoriteButton: PropTypes.bool,
    showShareButton: PropTypes.bool,
    showVolumeButton: PropTypes.bool,
    monthToString: PropTypes.func,
    getCurrentTimestamp: PropTypes.func,
    isAbleToHideSkin: PropTypes.bool.isRequired,
    userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
      .isRequired,
    playerWidth: PropTypes.number.isRequired,
    playerHeight: PropTypes.number.isRequired,

    // child components
    ShareWidget: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.instanceOf(HTMLElement),
    ]),
    EpisodeWidget: PropTypes.oneOfType([
      PropTypes.func,
      PropTypes.instanceOf(HTMLElement),
    ]),
    socials: PropTypes.array,
    supportPhone: PropTypes.string,
    supportEmail: PropTypes.string,

    isFavoritePhrase: PropTypes.string,
    isRootHidden: PropTypes.bool.isRequired,
    onClickRemindIcon: PropTypes.func,
    remindInfo: PropTypes.object,
    playlistInfo: PropTypes.object,
    playlistItem: PropTypes.object,
  };

  static defaultProps = {
    // player's properties
    timeToAdStart: -1,
    nextObject: null,
    prevObject: null,
    subtitlesText: null,
    audioTrack: '',
    subtitles: '',
    quality: '',
    backgroundPoster: null,

    // // player's functions
    nextChannel: Function.prototype,
    prevChannel: Function.prototype,
    prevProgram: Function.prototype,
    nextProgram: Function.prototype,
    backOnAir: Function.prototype,
    skipIntroStart: Function.prototype,
    disableLoader: Function.prototype,

    // advert properties
    adVastData: null,
    adType: '',
    adCurrentTime: 0,
    adPlaying: false,
    adTimeLabels: [],

    // stream properties
    rootVideoId: null,
    title: null,
    programDuration: 0,
    programPosition: 0,
    currentProgramTime: 0,
    channelState: null,
    currentChannel: null,
    nextProgramObject: {},
    prevProgramObject: {},
    program: null,
    autoQualityIndex: undefined,
    playlistInfo: null,
    playlistItem: null,

    // miscellaneous properties
    playerVideoElement: null,
    ShareWidget: null,
    EpisodeWidget: null,
    socials: [],
    supportPhone: '',
    supportEmail: '',
    showCloseButton: false,
    showSkipIntroButton: false,
    skipIntroSkipped: false,
    showFavoriteButton: true,
    showShareButton: true,
    showVolumeButton: true,
    monthToString: Function.prototype,
    getCurrentTimestamp: Function.prototype,
    isFavoritePhrase: '',
  };

  constructor(props) {
    super(props);
    this.state = {
      hideSkin: false,
      subtitlePosition: 70,
      seeking: { value: false, type: 'seekbar' },
      rightSeek: false,
      leftSeek: false,
      isShareOpen: false,
      isEpisodeOpen: false,
      shareLinkByTime: null,
      dbtapSeekAmount: 0,
      isBigSkin: false,
    };

    this.startSeekPos = 0;
    this.tapTimeout = null;
    this.increment = 0;
    this.throttledSeek = null;
    this.secondsPrefix = '00:00:';
    this.minutesPrefix = '00:';
    this.widgetContainer = React.createRef();
    this.substrateRef = React.createRef();
    this.needToResumeAfterPopup = false;
    this.substrateClickTimeout = null;
    this.getSettingsPopupState = null;
    this.closeSettingsPopup = null;
    this.ShareMobileComponent = props.isMobile
      ? withMenu(MenuShare)
      : undefined;
    this.throttledRefreshTimer = _.throttle(this.refreshSkinTimer, 100, {
      trailing: false,
    });
    this.needToResetSkipAd = false;
    this.needToShowSkinByMessageMode = false;
    this.isDesktopEpgPresent = false;
    this.lastPointerType = null;

    this.props.onSharePopupOpen(this.onShareOpen);

    // const isTVPage = Boolean(document.querySelector('.tvVideoView_v1'));
    // const isNeedNewSkin = isTVPage && !this.props.isMobile;

    this.isNewSkin =
      !this.props.isMobile || Cookie.get('fullVersion') === 'true';

    this.SkinWrapper = React.lazy(() =>
      this.isNewSkin
        ? import(
            /* webpackChunkName: "mggp-desktop-skin" */ './DesktopSkin/DesktopSkin'
          )
        : import(
            /* webpackChunkName: "mggp-mobile-skin" */ './MobileSkin/MobileSkin'
          )
    );
  }

  componentDidMount() {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('interactiveenter', this.onHideInteractive);
    window.addEventListener('interactiveleave', this.onShowInteractive);
  }

  onShowInteractive = () => {
    // console.log('interactive request foreground');
    this.setState({
      hasInteractive: true,
    });
    this.hideSkinFunc('on show interactive');
    clearTimeout(this.RELEASE_FG_TIMER);
  };

  RELEASE_FG_TIMER = null;

  onHideInteractive = () => {
    // console.log('interactive release foreground');
    clearTimeout(this.RELEASE_FG_TIMER);
    this.RELEASE_FG_TIMER = setTimeout(() => {
      this.setState({
        hasInteractive: false,
      });
    }, 100);
  };

  UNSAFE_componentWillReceiveProps({
    timeToAdStart: nextTimeToAdStart,
    paused: nextPaused,
    isAbleToHideSkin: nextIsAbleToHideSkin,
    isFullscreen: nextIsFullscreen,
  }) {
    const {
      timeToAdStart: prevTimeToAdStart,
      paused,
      setSkipAdProperty,
      isAbleToHideSkin,
      isFullscreen,
    } = this.props;
    const { hideSkin } = this.state;
    if (
      hideSkin &&
      nextTimeToAdStart > 0 &&
      nextTimeToAdStart !== prevTimeToAdStart
    ) {
      this.showSkinFunc();
    }

    if (!hideSkin && prevTimeToAdStart === 1) {
      this.refreshSkinTimer();
    }

    if (this.needToResetSkipAd && !nextPaused && nextPaused !== paused) {
      this.needToResetSkipAd = false;
      setSkipAdProperty(false);
    }
    if (nextIsAbleToHideSkin !== isAbleToHideSkin) {
      this[isAbleToHideSkin ? 'refreshSkinTimer' : 'showSkinFunc']();
    }
    if (nextIsFullscreen && nextIsFullscreen !== isFullscreen) {
      this.isDesktopEpgPresent = getIsDesktopEpgPresent();
    }
  }

  componentDidUpdate(prevProps, prevState) {
    const isHideSkinChanged = prevState.hideSkin !== this.state.hideSkin;
    const isHiddenChanged = prevProps.isRootHidden !== this.props.isRootHidden;
    const isHiddenSkin = isHideSkinChanged
      ? this.state.hideSkin
      : this.props.isRootHidden;
    const isSmallModeChanged = prevProps.smallMode !== this.props.smallMode;
    const isEnded = this.props.ended && this.props.ended !== prevProps.ended;
    const isChannelChanged =
      prevProps.currentChannel &&
      prevProps.currentChannel.id !== this.props.currentChannel.id;

    if (prevProps.messageModeData !== this.props.messageModeData) {
      const { messageModeData } = this.props;
      if (
        typeof messageModeData === 'object' &&
        'externalEmbed' in (messageModeData || {}) &&
        messageModeData.externalEmbed === true
      ) {
        this.needToShowSkinByMessageMode = false;
      } else {
        this.needToShowSkinByMessageMode = Boolean(messageModeData);
      }

      if (this.needToShowSkinByMessageMode) {
        if (SKIN_TIMER_ID) {
          clearTimeout(SKIN_TIMER_ID);
          SKIN_TIMER_ID = null;
        }
        this.showSkinFunc('didUpdate');
      }
    }
    if (
      this.props.isMobile &&
      !this.props.isAndroid &&
      prevProps.adPlaying !== this.props.adPlaying
    ) {
      iOSFullscreenWatcher(this.props.adPlaying);
    }
    if (isHideSkinChanged || isHiddenChanged) {
      this.props.emitSkinVisibilityBeginChange(!isHiddenSkin);
    }

    if (isChannelChanged && this.state.isShareOpen) {
      this.onShareClose();
    }

    if (isSmallModeChanged && this.props.smallMode && this.state.isShareOpen) {
      this.onShareClose();
    }

    if (
      isEnded &&
      this.props.isFullscreen &&
      this.props.isMobile &&
      !this.props.isAndroid
    ) {
      this.props.exitFullscreen();
    }

    const { messageModeData, isFullscreen, exitFullscreen } = this.props;
    if (
      Boolean(messageModeData) &&
      !messageModeData.externalEmbed &&
      isFullscreen
    ) {
      exitFullscreen();
    }
  }

  componentWillUnmount() {
    if (SKIN_TIMER_ID) {
      clearTimeout(SKIN_TIMER_ID);
      SKIN_TIMER_ID = null;
    }
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('interactiveenter', this.onHideInteractive);
    window.removeEventListener('interactiveleave', this.onShowInteractive);
  }

  onKeyDown = (event) => {
    if (this.props.isInteractiveFullscreen) {
      return;
    }

    switch (event.code) {
      case 'ArrowUp': // up arrow
      case 'ArrowDown': // down arrow
        this.refreshSkinTimer();
        break;
      case 'ArrowRight': // right arrow
      case 'ArrowLeft': // left arrow
        if (document.querySelector('[data-player-role="seekBar"]')) {
          this.refreshSkinTimer();
        }
        break;
      default:
    }
  };

  onShareOpen = () => {
    // we can't use this.isDesktopEpgPresent flag here, because it's needed only in a fullscreen as a prop in the MainWrapper
    if (this.props.isMobile && !getIsDesktopEpgPresent()) {
      this.props.onClickShareButton();
    } else {
      const { ShareWidget } = this.props;
      if (ShareWidget instanceof HTMLElement && this.widgetContainer) {
        ShareWidget.onClose = this.onShareClose;
        this.widgetContainer.appendChild(ShareWidget);

        // FOR FURTHER USING
        // now, 31.01.2019, the promise is not rejected when user canceled the sharing
        // and there is no way to detect that share window has closed

        // } else if (typeof window.navigator.share === 'function') {
        //   if (!this.nativeShareIsOpen) {
        //     this.nativeShareIsOpen = !!window.navigator
        //       .share({
        //         title: this.props.title,
        //         text: this.context.translateFunc(this.context.watchOnMgg),
        //         url: window.location.href,
        //       })
        //       .finally(() => {
        //         this.nativeShareIsOpen = false;
        //       });
        //   }
      } else {
        this.setState({
          isShareOpen: true,
        });
      }
      this.props.onShareOpen();
      this.handlePlayPauseOnPopup(true);
    }

    playerMethods.onShareWidgetShowed();
  };

  canBeSharedByTime = () => {
    const { contentType, program } = this.props;
    return (
      ['tv_dvr_catchup', 'm_ch_vod'].includes(contentType) ||
      (contentType === 'tv_dvr' && program.state === 0)
    );
  };

  onShareLinkByTimeChanged = (value) => {
    let target = null;
    if (value) {
      const { shareLink, program, currentTime, programPosition, channelState } =
        this.props;
      const targetTime = parseInt(
        program.startTimestamp +
          (channelState === 'timeshift' ? programPosition : currentTime)
      );
      target = `${shareLink}?est=${targetTime}&eet=${targetTime}`;

      playerMethods.onShareWidgetTimestampOn();
    }
    this.setState({
      shareLinkByTime: target,
    });
  };

  onShareClose = () => {
    const { ShareWidget } = this.props;
    if (ShareWidget instanceof HTMLElement && ShareWidget.parentElement) {
      ShareWidget.parentElement.removeChild(ShareWidget);
    } else {
      this.setState({
        isShareOpen: false,
        shareLinkByTime: null,
      });
    }
    this.props.onShareClose();
    this.handlePlayPauseOnPopup(false);
  };

  onEpisodeOpen = () => {
    const { EpisodeWidget } = this.props;
    if (_.isFunction(EpisodeWidget)) {
      this.setState({
        isEpisodeOpen: true,
      });
    } else if (EpisodeWidget instanceof HTMLElement && this.widgetContainer) {
      EpisodeWidget.onClose = this.onEpisodeClose;
      this.widgetContainer.appendChild(EpisodeWidget);
    }
    this.props.onEpisodeOpen();
    this.handlePlayPauseOnPopup(true);
  };

  onEpisodeClose = () => {
    const { EpisodeWidget } = this.props;
    if (_.isFunction(EpisodeWidget)) {
      this.setState({
        isEpisodeOpen: false,
      });
    } else if (
      EpisodeWidget instanceof HTMLElement &&
      EpisodeWidget.parentElement
    ) {
      EpisodeWidget.parentElement.removeChild(EpisodeWidget);
    }
    this.props.onEpisodeClose();
    this.handlePlayPauseOnPopup(false);
  };

  onMessageContainerRef = (ref) => {
    this.props.setMessageContainer(ref);
  };

  getSeeking = (seekingValue, seekingType) => {
    const { seeking } = this.state;

    if (
      (seeking.value && !seekingValue && seeking.type === seekingType) ||
      !seeking.value
    ) {
      this.setState({ seeking: { value: seekingValue, type: seekingType } });
    }
  };

  setSettingsPopupStateCb = (cb) => {
    this.getSettingsPopupState = cb;
  };

  setCloseSettingsPopupCb = (cb) => {
    this.closeSettingsPopup = cb;
  };

  handlePlayPauseOnPopup = (isOpen) => {
    const { play, pause, paused, setSkipAdProperty } = this.props;
    if (isOpen) {
      if (!paused) {
        setSkipAdProperty(true);
        pause();
        this.needToResumeAfterPopup = true;
      }
    } else if (this.needToResumeAfterPopup) {
      play();
      this.needToResumeAfterPopup = false;
    }
  };

  showSkinFunc = () => {
    this.setState({
      hideSkin: false,
      subtitlePosition: this.getSubtitlesPositionOnVisibleSkin({
        isBigSkin: this.state.isBigSkin,
        isSkinHidden: false,
      }),
    });
  };

  hideSkinFunc = () => {
    const { timeToAdStart, messageModeData, isAbleToHideSkin, isMobile } =
      this.props;
    if (
      timeToAdStart <= 0 &&
      !messageModeData &&
      (isAbleToHideSkin || isMobile)
    ) {
      this.setState({
        hideSkin: true,
        subtitlePosition: this.getSubtitlesPositionOnVisibleSkin({
          isBigSkin: this.state.isBigSkin,
          isSkinHidden: true,
        }),
      });
    }
  };

  startupSkinTimer = () => {
    if (!STARTUP_SKIN_TIMER_ID) {
      STARTUP_SKIN_TIMER_ID = setTimeout(this.hideSkinFunc, HIDE_SKIN_TIME);
    }
  };

  refreshSkinTimer = () => {
    if (!this.props.isRootHidden) {
      clearTimeout(SKIN_TIMER_ID);
      this.showSkinFunc(`refreshSkinTimer ${this.props.isRootHidden}`);

      SKIN_TIMER_ID = setTimeout(() => {
        SKIN_TIMER_ID = null;
        this.hideSkinFunc();
      }, HIDE_SKIN_TIME);
    }
  };

  getDesktopSettingsPopupState = () =>
    typeof this.getSettingsPopupState === 'function' &&
    this.getSettingsPopupState();

  getSubtitlesPositionOnVisibleSkin = (data) => {
    const { isBigSkin, isSkinHidden } = data;
    let subtitlePosition;

    if (isSkinHidden) {
      subtitlePosition = this.props.isMobile ? 35 : 20;
    } else {
      if (this.props.isMobile) {
        subtitlePosition = 85;
      } else {
        subtitlePosition = isBigSkin ? 220 : 170;
      }
    }

    return subtitlePosition;
  };

  handlePlayerClick = () => {
    if (this.getDesktopSettingsPopupState()) {
      // TODO: think about to refactor this
      this.closeSettingsPopup();
    } else if (this.substrateClickTimeout) {
      window.clearTimeout(this.substrateClickTimeout);
      this.substrateClickTimeout = null;
      const { isFullscreen, exitFullscreen, enterFullscreen } = this.props;
      if (isFullscreen) {
        exitFullscreen();
      } else {
        enterFullscreen();
      }
    } else {
      this.substrateClickTimeout = window.setTimeout(() => {
        const { play, pause, isMobile, paused } = this.props;
        this.substrateClickTimeout = null;
        if (!isMobile) {
          if (paused) {
            play();
          } else {
            pause();
          }
        }
      }, 300);
    }
  };

  handleMobilePointerDown = () => {
    const { hideSkin } = this.state;
    if (hideSkin) {
      this.refreshSkinTimer();
    } else if (this.getDesktopSettingsPopupState()) {
      this.closeSettingsPopup();
    } else {
      this.hideSkinFunc('on handle mobile pointer');
    }
  };

  onSkinSizeChanged = (skinSizesInfo) => {
    this.setState({
      isBigSkin: skinSizesInfo.isBigSkin,
      subtitlePosition: this.getSubtitlesPositionOnVisibleSkin({
        isBigSkin: skinSizesInfo.isBigSkin,
        isSkinHidden: this.state.hideSkin,
      }),
    });
  };

  onPointerEvent = (event) => {
    let pointerType;

    if (event.type === 'click') {
      pointerType = this.lastPointerType || event.nativeEvent.pointerType;
      // because of Safari on iOS, it has pointerType === 'mouse' in click event
    } else {
      pointerType = event.nativeEvent.pointerType;
      this.lastPointerType = pointerType;
    }

    const isMouse = pointerType === 'mouse';

    switch (event.type) {
      case 'click':
        if (isMouse) {
          this.handlePlayerClick();
        } else {
          this.handleMobilePointerDown();
        }
        break;
      case 'pointerenter':
        if (isMouse) {
          this.refreshSkinTimer();
        }
        break;
      case 'pointerleave':
      case 'pointercancel':
        if (isMouse) {
          this.hideSkinFunc();
        }
        break;
      case 'pointermove':
        if (isMouse) {
          this.throttledRefreshTimer();
        }
        break;
    }
  };

  render() {
    const {
      hideSkin,
      seeking,
      isShareOpen,
      shareLinkByTime,
      isEpisodeOpen,
      hasInteractive,
      isBigSkin,
    } = this.state;

    const {
      isWatermarkClickable,
      ShareWidget,
      isFullscreen,
      isMobile,
      isLoaded,
      ended,
      paused,
      pause,
      type,
      srcWaterMark,
      shareLink,
      translate,
      onRetry,
      EpisodeWidget,
      subtitlesText,
      adVastData,
      adCurrentTime,
      adPlaying,
      adContainer,
      adActionClick,
      adActionClose,
      adActionResume,
      isAdLinear,
      contentType,
      channelState,
      currentChannel,
      showSkipIntroButton,
      skipIntroSkipped,
      socials,
      supportEmail,
      supportPhone,
      messageModeData,
      nextChannel,
      prevChannel,
      skipIntroStart,
      disableLoader,
      smallMode,
      isRootHidden,
      playerWidth,
      playerHeight,
      isLinearHybridAdShowed,
    } = this.props;
    const ShareWidgetComponent = ShareWidget || ShareWidgetDefault;

    const skinPointerEvents =
      process.env.TEST || this.needToShowSkinByMessageMode || hasInteractive
        ? {}
        : {
            onPointerEnter: this.onPointerEvent,
            onPointerLeave: this.onPointerEvent,
            onPointerMove: this.onPointerEvent,
            onPointerCancel: this.onPointerEvent,
            onPointerDown: this.onPointerEvent,
            onPointerUp: this.onPointerEvent,
          };

    const showBackOnAir = Boolean(
      currentChannel &&
        contentType !== 'tv_linear' &&
        (channelState === 'catchup' || channelState === 'timeshift') &&
        !messageModeData
    );

    const isTV = type === 'tv';

    /*
        skinLogger('render. props: %O', this.props);
    */

    if (!isLoaded && STARTUP_SKIN_TIMER_ID) {
      clearTimeout(STARTUP_SKIN_TIMER_ID);
      STARTUP_SKIN_TIMER_ID = null;
    }

    (isLoaded || (messageModeData || {}).externalEmbed) &&
      !STARTUP_SKIN_TIMER_ID &&
      !this.needToShowSkinByMessageMode &&
      this.startupSkinTimer();

    const isSkinHidden =
      hideSkin &&
      !isAllwaysShowSkinDev &&
      !this.needToShowSkinByMessageMode &&
      !(isLinearHybridAdShowed && smallMode); // MHP-1889

    return (
      <SkinResizeObserver onSkinSizeChanged={this.onSkinSizeChanged}>
        <PlayerSkinStyled
          className={cn({
            skin_hidden: isSkinHidden,
            big_skin: isBigSkin,
          })}
          isMobile={isMobile}
          isHidden={isRootHidden}
          isSmallMode={smallMode}
          isFullscreen={isFullscreen}
        >
          {isMobile && isTV && (
            <SwipeChannelSwitch
              swipeAreaElement={this.substrateRef.current}
              toNextChannel={nextChannel}
              toPrevChannel={prevChannel}
            />
          )}
          <MainWrapper
            isTV={isTV}
            {...skinPointerEvents}
            isFullscreen={isFullscreen}
            isDesktopEpgPresent={this.isDesktopEpgPresent}
            isSmallMode={smallMode}
            isNeedHideOnEnd={ended && !messageModeData}
            isMobile={isMobile}
            hideSkin={hideSkin}
          >
            {!messageModeData && (
              <Substrate
                onClick={!smallMode ? this.onPointerEvent : undefined}
                smallMode={!this.isNewSkin && smallMode}
                hideSkin={hideSkin}
                ref={this.substrateRef}
              />
            )}
            {!messageModeData && !smallMode && (
              <SkipIntro
                isShowSkipIntroButton={showSkipIntroButton}
                isSkipIntroSkipped={skipIntroSkipped}
                skipIntroStart={skipIntroStart}
                disableLoader={disableLoader}
                isMobile={isMobile}
                isNewSkin={this.isNewSkin}
              />
            )}
            <MessageContainerStyled
              ref={this.onMessageContainerRef}
              visible={Boolean(messageModeData)}
            />
            <Suspense fallback={<div />}>
              <this.SkinWrapper
                {...this.props}
                onSeek={this.props.seek}
                isAllwaysShowSkinDev={isAllwaysShowSkinDev}
                isSkinHidden={isSkinHidden}
                isSeeking={seeking.value}
                isShowBackOnAir={showBackOnAir}
                isNeedToShowSkinByMessageMode={this.needToShowSkinByMessageMode}
                refreshTimerCallback={this.refreshSkinTimer}
                onEpisodeOpen={this.onEpisodeOpen}
                onShareOpen={this.onShareOpen}
                getSeeking={this.getSeeking}
                setSettingsPopupStateCb={this.setSettingsPopupStateCb}
                setCloseSettingsPopupCb={this.setCloseSettingsPopupCb}
              >
                <SafeZone.Spacer skinHidden={isSkinHidden} zoneId="safe_zone" />
              </this.SkinWrapper>
            </Suspense>
            <SafeZone.Container zoneId="safe_zone" />
            <SafeZone.Container zoneId="default" />
            {srcWaterMark && (
              <WaterMarkWrap
                isMobile={isMobile}
                topPosition={!(hideSkin && !seeking.value)}
              >
                <WaterMark
                  srcWaterMark={srcWaterMark}
                  clickable={isWatermarkClickable}
                  url={shareLink}
                />
              </WaterMarkWrap>
            )}
            {!smallMode && (
              <Subtitles
                isFullscreen={isFullscreen}
                subtitlePosition={this.state.subtitlePosition}
                subtitlesText={subtitlesText}
              />
            )}
            {/* TODO: continue here */}
            {isAdLinear === false && (
              <AdOverlay
                close={adActionClose}
                click={adActionClick}
                resume={adActionResume}
                playerPaused={paused}
                playerPause={pause}
                skinIsVisible={!hideSkin}
                position={Math.floor(adCurrentTime)}
                playerDims={{
                  width: playerWidth,
                  height: playerHeight,
                }}
                isMobile={isMobile}
                isNewSkin={this.isNewSkin}
                isBigSkin={isBigSkin}
                overlayInfo={{
                  horizontalPosition: _.get(
                    adVastData,
                    'horizontalAlign[0]',
                    ''
                  ),
                  scalable:
                    _.get(adVastData, 'mediaFile[0].scalable', 'false') ===
                    'true',
                  skipTime2: _.get(adVastData, 'skipTime2[0]'),
                  playing: adPlaying,
                  clickThroughLink: _.get(
                    adVastData,
                    'nonlinearclickthrough[0]',
                    ''
                  ),
                  isClickable: !!_.get(
                    adVastData,
                    'nonlinearclickthrough[0]',
                    ''
                  ),
                }}
                adContainer={adContainer}
              />
            )}
          </MainWrapper>

          {ended && !channelState && !messageModeData && (
            <PlayerSkinEnded
              onRetry={onRetry}
              translate={translate}
              contentType={contentType}
              playerWidth={playerWidth}
            />
          )}

          <ShareContainer ref={this.widgetContainer}>
            {isShareOpen && (
              <TranslateMapContext.Consumer>
                {(translateKeys) =>
                  isMobile ? (
                    <this.ShareMobileComponent
                      onClose={this.onShareClose}
                      socials={socials}
                      shareLink={shareLink}
                      shareText={translate(translateKeys.watchOnMgg)}
                      titleText={translate(translateKeys.shareButton)}
                    />
                  ) : (
                    <ShareWidgetComponent
                      isFullscreen={isFullscreen}
                      onClose={this.onShareClose}
                      canBeSharedByTime={this.canBeSharedByTime()}
                      canBeSharedByTimeSelected={Boolean(shareLinkByTime)}
                      onShareLinkByTimeChanged={this.onShareLinkByTimeChanged}
                      translate={translate}
                      shareLink={shareLinkByTime || shareLink}
                      supportEmail={supportEmail}
                      supportPhone={supportPhone}
                      socials={socials}
                      translateKeys={translateKeys}
                    />
                  )
                }
              </TranslateMapContext.Consumer>
            )}
            {isEpisodeOpen && _.isFunction(EpisodeWidget) && (
              <EpisodeWidget onClose={this.onEpisodeClose} />
            )}
          </ShareContainer>
        </PlayerSkinStyled>
      </SkinResizeObserver>
    );
  }
}

export default withTheme(PlayerSkin);
